import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Grid, Stack } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { FormProvider, RHFAutocomplete, RHFTextField } from 'src/components';
import { NormalUploader } from 'src/components/NormalUploader';
import { Paths } from 'src/config';
import { useDebounce } from 'src/hooks/useDebounce';
// import { ZONE_LIST } from 'src/helpers';
import { useGetCoursesQuery } from 'src/redux/services/CoursesServices';
import { useCreatePackageMutation } from 'src/redux/services/PackageServices';
import * as Yup from 'yup';

const defaultValues = {
  title: '',
  description: '',
  score: 0,
  courses: [],
  mobileCover: { name: '', hash: '' },
  desktopCover: { name: '', hash: '' },
  zone: [],
};

const PackageCreateValidationSchema = Yup.object({
  title: Yup.string().required('نام اجباری می باشد'),
  description: Yup.string().required('اسلاگ اجباری می باشد'),
  score: Yup.number().min(0, 'امتیاز ویژه باید بیشتر از ۱ باشد').required('امتیاز ویژه اجباری می باشد'),
  courses: Yup.array(Yup.mixed())
    .required('دوره های زیر مجموعه اجباری می باشد')
    .min(1, 'تعداد دوره های زیر مجموعه حداقل باید ۱ باشد'),
  mobileCover: Yup.object({
    name: Yup.string().required('نام کاور (حالت موبایل) اجباری می باشد'),
    hash: Yup.string().required('تصویر کاور (حالت موبایل) اجباری می باشد'),
  }),
  desktopCover: Yup.object({
    name: Yup.string().required('نام کاور (حالت دسکتاپ) اجباری می باشد'),
    hash: Yup.string().required('تصویر کاور (حالت دسکتاپ) اجباری می باشد'),
  }),
  // zone: Yup.array(Yup.mixed()).min(1, 'زون ها اجباری می باشد'),
}).required();

export const PackageCreateForm = () => {
  const [isCoursesListOpen, setIsCoursesListOpen] = useState(false);
  const [coursesQuery, setCoursesQuery] = useState({
    title: '',
  });
  const navigate = useNavigate();
  const [createPackage, { isLoading }] = useCreatePackageMutation();

  const debouncedCourseTitle = useDebounce(coursesQuery.title, 1000);

  const { data: coursesData, isFetching } = useGetCoursesQuery(
    { page: 1, limit: 30, title: debouncedCourseTitle },
    { skip: !isCoursesListOpen }
  );
  const methods = useForm({
    defaultValues,
    resolver: yupResolver(PackageCreateValidationSchema),
  });
  const { handleSubmit } = methods;

  function handleOpenCoursesListOpen() {
    setIsCoursesListOpen(true);
  }
  function handleCloseCoursesListOpen() {
    setIsCoursesListOpen(false);
  }
  async function onSubmit(values) {
    try {
      const body = {
        title: values.title,
        description: values.description,
        score: values.score,
        courses: [...new Set(values.courses)].map(({ _id }) => _id),
        mobileCover: values.mobileCover,
        desktopCover: values.desktopCover,
        // zone: values.zone?.map(({ value }) => value),
      };
      await createPackage(body).unwrap();
      enqueueSnackbar(`پکیج ${values.name} ایجاد شد`, { variant: 'success' });
      navigate(Paths.package.list.path);
    } catch (error) {
      console.log('CREATE PACKAGE ERROR', error);
      if (error?.data?.err === `package with name ${values.name} already exists!`) {
        enqueueSnackbar(`پکیج با نام ${values.name} قبلا ایجاد شده است`, { variant: 'warning' });
      } else if (error?.data?.err === `package with slug ${values.slug} already exists!`) {
        enqueueSnackbar(`پکیج با اسلاگ ${values.slug} قبلا ایجاد شده است`, { variant: 'warning' });
      } else {
        enqueueSnackbar(`پکیج ${values.title} ایجاد نشد`, { variant: 'error' });
      }
    }
  }

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <RHFTextField name={'title'} label={'نام'} />
        </Grid>
        <Grid item xs={6}>
          <RHFTextField name={'score'} label={'امتیاز ویژه'} type="number" InputProps={{ inputProps: { min: 0 } }} />
        </Grid>
        <Grid item xs={12}>
          <RHFTextField name={'description'} label={'توضیحات'} multiline minRows={5} />
        </Grid>
        {/* <Grid item xs={12}>
          <RHFAutocomplete
            name={'zone'}
            label={'زون ها'}
            multiple
            options={ZONE_LIST}
            defaultValue={''}
            disableClearable
            filterSelectedOptions
            getOptionDisabled={(option) =>
              methods
                .watch()
                .zone?.map(({ value }) => value)
                .includes(option.value)
            }
            getOptionLabel={(option) => option?.label}
            onChange={(event, newValue) => {
              methods.setValue('zone', newValue);
            }}
          />
        </Grid> */}
        <Grid item xs={12}>
          <RHFAutocomplete
            multiple
            name={'courses'}
            label={'دوره های زیر مجموعه'}
            options={coursesData?.courses || []}
            defaultValue={''}
            disableClearable
            filterSelectedOptions
            getOptionLabel={(option) => option?.title}
            onChange={(event, newValue) => {
              methods.setValue('courses', newValue);
            }}
            onInputChange={(event) => {
              setCoursesQuery({ title: event.target.value });
            }}
            getOptionDisabled={(option) =>
              methods
                .watch('courses')
                ?.map(({ _id }) => _id)
                .includes(option._id)
            }
            loading={isFetching}
            open={isCoursesListOpen}
            onOpen={handleOpenCoursesListOpen}
            onClose={handleCloseCoursesListOpen}
          />
        </Grid>
        <Grid item xs={6}>
          <Stack spacing={1.5}>
            <RHFTextField name={'desktopCover.name'} label={'نام کاور (حالت دسکتاپ)'} />
            <NormalUploader
              label="بارگذاری کاور (حالت دسکتاپ)"
              media={methods.watch().desktopCover.hash}
              callback={({ mediaHash }) => methods.setValue('desktopCover.hash', mediaHash)}
              ratio="4/3"
              error={methods.getFieldState('desktopCover.hash')?.error?.message}
            />
          </Stack>
        </Grid>
        <Grid item xs={6}>
          <Stack spacing={1.5}>
            <RHFTextField name={'mobileCover.name'} label={'نام کاور (حالت موبایل)'} />
            <NormalUploader
              label="بارگذاری کاور (حالت موبایل)"
              media={methods.watch().mobileCover.hash}
              callback={({ mediaHash }) => methods.setValue('mobileCover.hash', mediaHash)}
              ratio="3/4"
              error={methods.getFieldState('mobileCover.hash')?.error?.message}
            />
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Button variant="contained" disabled={isLoading} type="submit">
            {Paths.package.create.title}
          </Button>
        </Grid>
      </Grid>
    </FormProvider>
  );
};
